/**
 * Shared Frame Rendering Module
 *
 * Extracted from gallery-wall-frame-mock.js and room-background-mock.js to eliminate
 * duplication. Ported from Svelte FrameSlot.svelte with improved 3D shading, inner
 * bevels, glass reflection, mat texture, and multi-layer drop shadows.
 */

// Module-level texture cache shared across all instances (capped to prevent unbounded growth)
const TEXTURE_CACHE_MAX = 50;
const textureCache = new Map();

// Static procedural tiles, cached for the page lifetime (deliberately outside
// the LRU above — see generateCanvasTextureTile / renderMatTexture).
let _canvasTextureTile = null;
let _matDotTile = null;

// url → in-flight loadImage promise, so concurrent callers share one fetch.
const _imageLoadsInFlight = new Map();

// Cache for rotated texture canvases (keyed by source texture)
const rotatedTextureCache = new WeakMap();
// Cache for horizontally-flipped texture canvases (keyed by source texture).
// Tiles are extracted from the right edge so x=0 = inner, x=texW = outer.
// The renderer draws each rail with x=0 at the outer edge, so we flip once
// here rather than per-side to keep the draw calls uniform.
const flippedTextureCache = new WeakMap();

// Per-rail lighting weights. Match the 3D PBR render's IBL pattern under
// RoomEnvironment: the bottom rail's chamfer reflects the envmap's floor-
// bounce region most strongly (brightest); top catches ceiling light; sides
// are dimmer. Used by both the env-boost overlay and the profile shading.
const RAIL_LIGHT = { bottom: 1.0, top: 0.9, right: 0.78, left: 0.75 };

// Warm studio tint applied via the env-boost overlay. Pure white washes out
// the frame; a faint warm cream approximates the cast the PBR material picks
// up from the RoomEnvironment. Kept very close to white — the warmth should
// be perceptible, not dominant.
const ENV_TINT_RGB = '255, 252, 244';

/**
 * Load an image with caching.
 * @param {string} url
 * @returns {Promise<HTMLImageElement|null>}
 */
export function loadImage(url) {
  if (!url) return Promise.resolve(null);

  const cached = textureCache.get(url);
  if (cached) {
    // Move to end for LRU ordering (Map iterates in insertion order)
    textureCache.delete(url);
    textureCache.set(url, cached);
    return Promise.resolve(cached);
  }

  // In-flight dedup: a dozen frame-drawer tiles requesting the same moulding
  // URL in one tick each started their own Image load and each ran the LRU
  // eviction below — evicting up to N distinct cached entries for one URL.
  const inFlight = _imageLoadsInFlight.get(url);
  if (inFlight) return inFlight;

  const load = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      _imageLoadsInFlight.delete(url);
      // Evict oldest entry if at capacity
      if (textureCache.size >= TEXTURE_CACHE_MAX) {
        const oldest = textureCache.keys().next().value;
        textureCache.delete(oldest);
      }
      textureCache.set(url, img);
      resolve(img);
    };
    img.onerror = () => {
      _imageLoadsInFlight.delete(url);
      resolve(null);
    };
    img.src = url;
  });
  _imageLoadsInFlight.set(url, load);
  return load;
}

/**
 * Get a cached texture image (synchronous).
 * @param {string} url
 * @returns {HTMLImageElement|null}
 */
export function getCachedImage(url) {
  return textureCache.get(url) || null;
}

/**
 * Compute luminance-based miter line opacity.
 * Dark frames get subtle miters (~0.05), light frames get visible miters (~0.20).
 * @param {string} hexColor - Frame color as hex string (e.g. '#c4a574')
 * @returns {number} Opacity value between 0.05 and 0.20
 */
export function computeMiterOpacity(hexColor) {
  const hex = (hexColor || '#2d2d2d').replace('#', '');
  const val = parseInt(hex, 16);
  const r = (val >> 16) & 255;
  const g = (val >> 8) & 255;
  const b = val & 255;
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return 0.05 + 0.15 * (1 - lum);
}

/**
 * Compute relative luminance (0 = black, 1 = white) from a hex color.
 * Used to scale highlight effects — dark frames get near-zero highlights.
 * @param {string} hexColor - Frame color as hex string (e.g. '#2d2d2d')
 * @returns {number} Luminance value between 0 and 1
 */
function luminance(hexColor) {
  const hex = (hexColor || '#808080').replace('#', '');
  const val = parseInt(hex, 16);
  const r = (val >> 16) & 255;
  const g = (val >> 8) & 255;
  const b = val & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/**
 * 3D shading gradient overlays for frame depth.
 * Ported from Svelte FrameSlot.svelte with improved asymmetric lighting:
 * - Light sides (top/left): barely visible (0.02-0.03 white)
 * - Shadow sides (bottom/right): strong (0.12-0.14 black)
 * - Inner bevel strips along inside frame edges
 * - Dynamic inner edge shadows scaled by frameDepth
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} width - Total frame width in px
 * @param {number} height - Total frame height in px
 * @param {number} fw - Frame rail width in px
 * @param {number} frameDepth - Depth factor 0-1 (default 0.5)
 */
export function add3DShading(ctx, width, height, fw, frameDepth = 0.5, frameColor = '') {
  // --- TOP EDGE (light side) --- barely visible, mostly flat
  // Scale highlight by frameDepth AND frame luminance — near-zero for dark frames
  const lum = luminance(frameColor);
  const hlAlpha = 0.015 * frameDepth * lum;
  const topGrad = ctx.createLinearGradient(0, 0, 0, fw);
  topGrad.addColorStop(0, `rgba(255,255,255,${hlAlpha.toFixed(4)})`);
  topGrad.addColorStop(0.15, 'rgba(255,255,255,0)');
  topGrad.addColorStop(0.85, 'rgba(0,0,0,0)');
  topGrad.addColorStop(1, 'rgba(0,0,0,0.02)');
  ctx.fillStyle = topGrad;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(width, 0);
  ctx.lineTo(width - fw, fw);
  ctx.lineTo(fw, fw);
  ctx.closePath();
  ctx.fill();

  // --- LEFT EDGE (light side) --- barely visible, mostly flat
  const leftGrad = ctx.createLinearGradient(0, 0, fw, 0);
  leftGrad.addColorStop(0, `rgba(255,255,255,${(hlAlpha * 0.7).toFixed(4)})`);
  leftGrad.addColorStop(0.15, 'rgba(255,255,255,0)');
  leftGrad.addColorStop(0.85, 'rgba(0,0,0,0)');
  leftGrad.addColorStop(1, 'rgba(0,0,0,0.02)');
  ctx.fillStyle = leftGrad;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(fw, fw);
  ctx.lineTo(fw, height - fw);
  ctx.lineTo(0, height);
  ctx.closePath();
  ctx.fill();

  // --- BOTTOM EDGE (shadow side) --- strong directional shadow
  const bottomGrad = ctx.createLinearGradient(0, height - fw, 0, height);
  bottomGrad.addColorStop(0, 'rgba(0,0,0,0)');
  bottomGrad.addColorStop(0.3, 'rgba(0,0,0,0.02)');
  bottomGrad.addColorStop(0.7, 'rgba(0,0,0,0.06)');
  bottomGrad.addColorStop(1, 'rgba(0,0,0,0.14)');
  ctx.fillStyle = bottomGrad;
  ctx.beginPath();
  ctx.moveTo(0, height);
  ctx.lineTo(fw, height - fw);
  ctx.lineTo(width - fw, height - fw);
  ctx.lineTo(width, height);
  ctx.closePath();
  ctx.fill();

  // --- RIGHT EDGE (shadow side) --- moderate directional shadow
  const rightGrad = ctx.createLinearGradient(width - fw, 0, width, 0);
  rightGrad.addColorStop(0, 'rgba(0,0,0,0)');
  rightGrad.addColorStop(0.3, 'rgba(0,0,0,0.02)');
  rightGrad.addColorStop(0.7, 'rgba(0,0,0,0.05)');
  rightGrad.addColorStop(1, 'rgba(0,0,0,0.12)');
  ctx.fillStyle = rightGrad;
  ctx.beginPath();
  ctx.moveTo(width, 0);
  ctx.lineTo(width - fw, fw);
  ctx.lineTo(width - fw, height - fw);
  ctx.lineTo(width, height);
  ctx.closePath();
  ctx.fill();

  // --- INNER BEVEL --- chamfered lip on the inside edge of the frame moulding
  const bevelWidth = fw * 0.2 * frameDepth;
  if (bevelWidth > 0.5) {
    // Top inner bevel (dark strip along inside top edge)
    const topBevelGrad = ctx.createLinearGradient(fw, fw - bevelWidth, fw, fw);
    topBevelGrad.addColorStop(0, 'rgba(0,0,0,0.02)');
    topBevelGrad.addColorStop(0.5, `rgba(0,0,0,${(0.15 * frameDepth).toFixed(3)})`);
    topBevelGrad.addColorStop(1, `rgba(0,0,0,${(0.25 * frameDepth).toFixed(3)})`);
    ctx.fillStyle = topBevelGrad;
    ctx.fillRect(fw, fw - bevelWidth, width - fw * 2, bevelWidth);

    // Left inner bevel
    const leftBevelGrad = ctx.createLinearGradient(fw - bevelWidth, fw, fw, fw);
    leftBevelGrad.addColorStop(0, 'rgba(0,0,0,0.02)');
    leftBevelGrad.addColorStop(0.5, `rgba(0,0,0,${(0.12 * frameDepth).toFixed(3)})`);
    leftBevelGrad.addColorStop(1, `rgba(0,0,0,${(0.2 * frameDepth).toFixed(3)})`);
    ctx.fillStyle = leftBevelGrad;
    ctx.fillRect(fw - bevelWidth, fw, bevelWidth, height - fw * 2);

    // Bottom inner bevel (lighter — light hits this edge)
    const bottomBevelGrad = ctx.createLinearGradient(fw, height - fw - bevelWidth, fw, height - fw);
    bottomBevelGrad.addColorStop(0, 'rgba(0,0,0,0.01)');
    bottomBevelGrad.addColorStop(1, `rgba(0,0,0,${(0.08 * frameDepth).toFixed(3)})`);
    ctx.fillStyle = bottomBevelGrad;
    ctx.fillRect(fw, height - fw - bevelWidth, width - fw * 2, bevelWidth);

    // Right inner bevel (lighter)
    const rightBevelGrad = ctx.createLinearGradient(width - fw - bevelWidth, fw, width - fw, fw);
    rightBevelGrad.addColorStop(0, 'rgba(0,0,0,0.01)');
    rightBevelGrad.addColorStop(1, `rgba(0,0,0,${(0.06 * frameDepth).toFixed(3)})`);
    ctx.fillStyle = rightBevelGrad;
    ctx.fillRect(width - fw - bevelWidth, fw, bevelWidth, height - fw * 2);
  }

  // --- INNER EDGE SHADOWS --- where frame meets mat/artwork
  const innerShadowSize = fw * (0.15 + 0.15 * frameDepth);

  // Top inner shadow (frame casting shadow on mat)
  const topInnerGrad = ctx.createLinearGradient(fw, fw, fw, fw + innerShadowSize);
  topInnerGrad.addColorStop(0, `rgba(0,0,0,${(0.12 + 0.08 * frameDepth).toFixed(3)})`);
  topInnerGrad.addColorStop(0.6, `rgba(0,0,0,${(0.04 * frameDepth).toFixed(3)})`);
  topInnerGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = topInnerGrad;
  ctx.fillRect(fw, fw, width - fw * 2, innerShadowSize);

  // Left inner shadow
  const leftInnerGrad = ctx.createLinearGradient(fw, fw, fw + innerShadowSize, fw);
  leftInnerGrad.addColorStop(0, `rgba(0,0,0,${(0.09 + 0.06 * frameDepth).toFixed(3)})`);
  leftInnerGrad.addColorStop(0.6, `rgba(0,0,0,${(0.03 * frameDepth).toFixed(3)})`);
  leftInnerGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = leftInnerGrad;
  ctx.fillRect(fw, fw, innerShadowSize, height - fw * 2);
}

/**
 * Build two profile-derived shading overlay gradients for a textured rail.
 *
 * Uses surface-normal lighting:
 *   slope < 0 (surface protruding toward viewer): highlight — leading face of bumps
 *   slope > 0 (surface receding from viewer):     shadow   — trailing face of bumps
 * Height provides ambient recession darkening.
 *
 * Shadow/highlight budgets are luminance-scaled so light frames get subtle shading
 * (matching real-world photos) while dark frames get stronger contrast.
 *
 * Returns { shadow: CanvasGradient, highlight: CanvasGradient }
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x0,y0 - Gradient start (outer edge of rail)
 * @param {number} x1,y1 - Gradient end (inner edge of rail)
 * @param {Array}  profilePoints
 * @param {string} railSide    - 'top'|'left'|'right'|'bottom'
 * @param {string} frameColor  - Hex color used to scale shadow/highlight budgets
 * @returns {{ shadow: CanvasGradient, highlight: CanvasGradient }|null}
 */
function buildProfileShadingOverlay(ctx, x0, y0, x1, y1, profilePoints, railSide, frameColor) {
  const sorted = normalizeProfilePoints(profilePoints);
  if (!sorted) return null;

  const lightScale = RAIL_LIGHT[railSide] ?? 1.0;

  // Luminance-scaled budgets: light frames need subtle shadows to read as
  // photoreal; dark frames need more contrast. Budgets doubled relative to
  // the original pass — the PBR-rendered 3D moulding produces a much more
  // dramatic chamfer gradient than the old budgets could approximate.
  const lum = luminance(frameColor || '#808080');  // 0=black, 1=white
  const maxAmbient  = 0.18 + 0.18 * (1 - lum);   // white→0.18, black→0.36
  const maxSlopeShd = 0.14 + 0.20 * (1 - lum);   // white→0.14, black→0.34
  const maxSlopeHlt = 0.16 + 0.24 * (1 - lum);   // white→0.16, black→0.40

  // 24 samples is enough for the smooth Bezier-derived profile curves —
  // gradients interpolate between stops anyway. The previous 64 produced
  // 130 colour stops per rail pair (520 per render across four rails),
  // which is a measurable gradient-rasterisation cost on mobile Safari
  // when a resize/zoom drives a render per frame.
  const N = 24;
  const DT = 1 / N;

  const heights = new Float32Array(N + 1);
  const slopes  = new Float32Array(N + 1);
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    heights[i] = sampleProfile(sorted, t);
    slopes[i]  = (sampleProfile(sorted, Math.min(1, t + DT)) -
                  sampleProfile(sorted, Math.max(0, t - DT))) / (2 * DT);
  }

  const yMin   = Math.min(...heights);
  const yMax   = Math.max(...heights);
  const yRange = Math.max(yMax - yMin, 0.05);

  const shadowGrad    = ctx.createLinearGradient(x0, y0, x1, y1);
  const highlightGrad = ctx.createLinearGradient(x0, y0, x1, y1);

  // Every rail's OUTER chamfer slope faces outward (away from the frame
  // centre), and under the 3D's RoomEnvironment IBL each rail reflects
  // whatever envmap region lies in that direction. Ceiling, floor, and side
  // walls all carry some light in RoomEnvironment, so the outer edge is the
  // bright edge on every rail — bottom most dramatically, from floor bounce.
  for (let i = 0; i <= N; i++) {
    const t  = i / N;
    const y  = heights[i];
    const s  = slopes[i];
    const yN = (y - yMin) / yRange;  // 0=fully protruding, 1=fully recessed

    const ambient  = maxAmbient  * yN;
    const slopeHlt = s > 0 ? Math.min(maxSlopeHlt, s * maxSlopeHlt * 0.9) : 0;
    const slopeShd = s < 0 ? Math.min(maxSlopeShd, -s * maxSlopeShd * 0.9) : 0;

    const totalShadow    = Math.min(maxAmbient + maxSlopeShd, (ambient + slopeShd) * lightScale);
    const totalHighlight = Math.max(0, slopeHlt * lightScale - ambient * 0.3);

    shadowGrad.addColorStop(t,    `rgba(0,0,0,${totalShadow.toFixed(3)})`);
    highlightGrad.addColorStop(t, `rgba(255,255,255,${totalHighlight.toFixed(3)})`);
  }

  return { shadow: shadowGrad, highlight: highlightGrad };
}

/**
 * Tile a moulding texture onto the four frame rails with miter corners and 3D shading.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} width - Total canvas width
 * @param {number} height - Total canvas height
 * @param {number} fw - Frame rail width in px
 * @param {HTMLImageElement} texture - Moulding tile image
 * @param {string} frameColor - Frame color hex (for miter opacity)
 * @param {number} frameDepth - Depth factor 0-1
 * @param {Array|null} profilePoints - Optional moulding cross-section points for per-profile shading
 */
export function renderTexturedFrameCanvas(ctx, width, height, fw, texture, frameColor, frameDepth = 0.5, profilePoints = null) {
  const useProfile = Array.isArray(profilePoints) && profilePoints.length > 0;
  // HDR environment boost: 3D PBR renders grey tiles near-white due to the
  // bright RoomEnvironment IBL. We lift the 2D canvas to match by overlaying
  // a warm cream (ENV_TINT_RGB) per rail, scaled by frame luminance and by
  // the per-rail IBL weight (bottom brightest, sides dimmest). Baseline
  // bumped from 0.35 to 0.55 so the overall exposure matches the PBR render.
  const frameLum = luminance(frameColor || '#808080');
  const baseEnvBoost = Math.max(0, frameLum * 0.38);
  const railEnvBoost = side => Math.min(0.5, baseEnvBoost * (RAIL_LIGHT[side] ?? 1.0));
  const texW = texture.width;
  const texH = texture.height;
  if (!texW || !texH) { renderSolidFrameCanvas(ctx, width, height, fw, frameColor, frameDepth); return; }

  // Tiles are extracted from the right edge: x=0=inner, x=texW=outer.
  // The rail draw code maps x=0 of the tile to the outer edge on every side,
  // so flip horizontally once here to align conventions.
  let flippedTexture = flippedTextureCache.get(texture);
  if (!flippedTexture) {
    flippedTexture = document.createElement('canvas');
    flippedTexture.width = texW;
    flippedTexture.height = texH;
    const fCtx = flippedTexture.getContext('2d');
    fCtx.translate(texW, 0);
    fCtx.scale(-1, 1);
    fCtx.drawImage(texture, 0, 0);
    flippedTextureCache.set(texture, flippedTexture);
  }
  texture = flippedTexture;

  // Scaling: texture width maps to frame rail width
  const scale = fw / texW;
  const tileLen = Math.round(texH * scale);
  if (tileLen <= 0) { renderSolidFrameCanvas(ctx, width, height, fw, frameColor, frameDepth); return; }
  const vertTiles = Math.ceil(height / tileLen) + 1;
  const horizTiles = Math.ceil(width / tileLen) + 1;
  const overlap = 1;

  // Get or create rotated texture for horizontal sides
  let rotatedCanvas = rotatedTextureCache.get(texture);
  if (!rotatedCanvas) {
    rotatedCanvas = document.createElement('canvas');
    rotatedCanvas.width = texH;
    rotatedCanvas.height = texW;
    const rotCtx = rotatedCanvas.getContext('2d');
    rotCtx.translate(texH, 0);
    rotCtx.rotate(Math.PI / 2);
    rotCtx.drawImage(texture, 0, 0);
    rotatedTextureCache.set(texture, rotatedCanvas);
  }

  const horizTileW = Math.round(texH * scale);
  const horizTileH = fw;

  // LEFT SIDE
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(fw, fw);
  ctx.lineTo(fw, height - fw);
  ctx.lineTo(0, height);
  ctx.closePath();
  ctx.clip();
  for (let i = 0; i < vertTiles; i++) {
    const y = Math.round(i * tileLen);
    ctx.drawImage(texture, 0, 0, texW, texH, 0, y - overlap, fw, tileLen + overlap * 2);
  }
  if (useProfile) {
    const eb = railEnvBoost('left');
    if (eb > 0.01) { ctx.fillStyle = `rgba(${ENV_TINT_RGB},${eb.toFixed(3)})`; ctx.fillRect(0, 0, fw, height); }
    const ov = buildProfileShadingOverlay(ctx, 0, 0, fw, 0, profilePoints, 'left', frameColor);
    if (ov) {
      ctx.fillStyle = ov.shadow;    ctx.fillRect(0, 0, fw, height);
      ctx.fillStyle = ov.highlight; ctx.fillRect(0, 0, fw, height);
    }
  }
  ctx.restore();

  // RIGHT SIDE (flipped) — transform makes x=0 the outer edge, x=fw the inner edge
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(width, 0);
  ctx.lineTo(width - fw, fw);
  ctx.lineTo(width - fw, height - fw);
  ctx.lineTo(width, height);
  ctx.closePath();
  ctx.clip();
  ctx.translate(width, 0);
  ctx.scale(-1, 1);
  for (let i = 0; i < vertTiles; i++) {
    const y = Math.round(i * tileLen);
    ctx.drawImage(texture, 0, 0, texW, texH, 0, y - overlap, fw, tileLen + overlap * 2);
  }
  if (useProfile) {
    const eb = railEnvBoost('right');
    if (eb > 0.01) { ctx.fillStyle = `rgba(${ENV_TINT_RGB},${eb.toFixed(3)})`; ctx.fillRect(0, 0, fw, height); }
    const ov = buildProfileShadingOverlay(ctx, 0, 0, fw, 0, profilePoints, 'right', frameColor);
    if (ov) {
      ctx.fillStyle = ov.shadow;    ctx.fillRect(0, 0, fw, height);
      ctx.fillStyle = ov.highlight; ctx.fillRect(0, 0, fw, height);
    }
  }
  ctx.restore();

  // TOP SIDE (rotated)
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(width, 0);
  ctx.lineTo(width - fw, fw);
  ctx.lineTo(fw, fw);
  ctx.closePath();
  ctx.clip();
  for (let i = 0; i < horizTiles; i++) {
    const x = Math.round(i * horizTileW);
    ctx.drawImage(rotatedCanvas, 0, 0, rotatedCanvas.width, rotatedCanvas.height,
      x - overlap, 0, horizTileW + overlap * 2, horizTileH);
  }
  if (useProfile) {
    const eb = railEnvBoost('top');
    if (eb > 0.01) { ctx.fillStyle = `rgba(${ENV_TINT_RGB},${eb.toFixed(3)})`; ctx.fillRect(0, 0, width, fw); }
    const ov = buildProfileShadingOverlay(ctx, 0, 0, 0, fw, profilePoints, 'top', frameColor);
    if (ov) {
      ctx.fillStyle = ov.shadow;    ctx.fillRect(0, 0, width, fw);
      ctx.fillStyle = ov.highlight; ctx.fillRect(0, 0, width, fw);
    }
  }
  ctx.restore();

  // BOTTOM SIDE (rotated + flipped) — transform makes y=0 the outer edge, y=fw the inner edge
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(0, height);
  ctx.lineTo(fw, height - fw);
  ctx.lineTo(width - fw, height - fw);
  ctx.lineTo(width, height);
  ctx.closePath();
  ctx.clip();
  ctx.translate(0, height);
  ctx.scale(1, -1);
  for (let i = 0; i < horizTiles; i++) {
    const x = Math.round(i * horizTileW);
    ctx.drawImage(rotatedCanvas, 0, 0, rotatedCanvas.width, rotatedCanvas.height,
      x - overlap, 0, horizTileW + overlap * 2, horizTileH);
  }
  if (useProfile) {
    const eb = railEnvBoost('bottom');
    if (eb > 0.01) { ctx.fillStyle = `rgba(${ENV_TINT_RGB},${eb.toFixed(3)})`; ctx.fillRect(0, 0, width, fw); }
    const ov = buildProfileShadingOverlay(ctx, 0, 0, 0, fw, profilePoints, 'bottom', frameColor);
    if (ov) {
      ctx.fillStyle = ov.shadow;    ctx.fillRect(0, 0, width, fw);
      ctx.fillStyle = ov.highlight; ctx.fillRect(0, 0, width, fw);
    }
    // Bottom-rail specular: in the 3D's RoomEnvironment IBL, the outer
    // chamfer of the bottom rail reflects the envmap's floor-bounce region,
    // producing a strong near-white highlight along the very bottom edge.
    // The profile shading alone can't reproduce that peak — layer a
    // dedicated gradient from outer (bright) → inner (clear). After the
    // ctx.translate/scale flip, y=0 is the outer edge and y=fw is the
    // inner edge.
    const specLum = luminance(frameColor || '#808080');
    const specPeak = 0.55 + 0.30 * specLum;  // dark→0.55, white→0.85
    const spec = ctx.createLinearGradient(0, 0, 0, fw);
    spec.addColorStop(0,    `rgba(${ENV_TINT_RGB},${specPeak.toFixed(3)})`);
    spec.addColorStop(0.45, `rgba(${ENV_TINT_RGB},${(specPeak * 0.15).toFixed(3)})`);
    spec.addColorStop(1,    `rgba(${ENV_TINT_RGB},0)`);
    ctx.fillStyle = spec;
    ctx.fillRect(0, 0, width, fw);
  }
  ctx.restore();

  // Miter lines — luminance-adaptive opacity
  const miterOpacity = computeMiterOpacity(frameColor);
  ctx.strokeStyle = `rgba(0,0,0,${miterOpacity.toFixed(3)})`;
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(fw, fw);
  ctx.moveTo(width, 0);
  ctx.lineTo(width - fw, fw);
  ctx.moveTo(0, height);
  ctx.lineTo(fw, height - fw);
  ctx.moveTo(width, height);
  ctx.lineTo(width - fw, height - fw);
  ctx.stroke();

  // Inner bevel + edge shadows — skip full add3DShading rail shading when profile handles it
  if (useProfile) {
    // Profile overlay already provides per-rail brightness variation.
    // Still draw the inner bevel and inner edge shadows from add3DShading.
    const bevelWidth = fw * 0.2 * frameDepth;
    if (bevelWidth > 0.5) {
      const bevelAlpha = 0.18 * frameDepth;
      ctx.fillStyle = `rgba(0,0,0,${bevelAlpha.toFixed(3)})`;
      ctx.fillRect(fw, fw - bevelWidth, width - fw * 2, bevelWidth);
      ctx.fillRect(fw - bevelWidth, fw, bevelWidth, height - fw * 2);
    }
  } else {
    add3DShading(ctx, width, height, fw, frameDepth, frameColor);
  }
}

/**
 * Render a solid-color frame with 3D shading (no texture).
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} width - Total frame width
 * @param {number} height - Total frame height
 * @param {number} fw - Frame rail width in px
 * @param {string} frameColor - Hex color
 * @param {number} frameDepth - Depth factor 0-1
 */
export function renderSolidFrameCanvas(ctx, width, height, fw, frameColor, frameDepth = 0.5, profilePoints = null) {
  const useProfile = Array.isArray(profilePoints) && profilePoints.length > 0;

  function fillRail(side) {
    if (useProfile) {
      // Gradient outer→inner derived from moulding cross-section
      const gradArgs = {
        top:    [ctx, 0,     0,      0,       fw,          profilePoints, frameColor, 'top'],
        bottom: [ctx, 0,     height, 0,       height - fw, profilePoints, frameColor, 'bottom'],
        left:   [ctx, 0,     0,      fw,      0,           profilePoints, frameColor, 'left'],
        right:  [ctx, width, 0,      width-fw, 0,          profilePoints, frameColor, 'right'],
      }[side];
      const g = buildProfileGradient(...gradArgs);
      ctx.fillStyle = g || frameColor;
    } else {
      ctx.fillStyle = frameColor;
    }
  }

  // Top
  fillRail('top');
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(width, 0);
  ctx.lineTo(width - fw, fw);
  ctx.lineTo(fw, fw);
  ctx.closePath();
  ctx.fill();

  // Bottom
  fillRail('bottom');
  ctx.beginPath();
  ctx.moveTo(0, height);
  ctx.lineTo(width, height);
  ctx.lineTo(width - fw, height - fw);
  ctx.lineTo(fw, height - fw);
  ctx.closePath();
  ctx.fill();

  // Left
  fillRail('left');
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(fw, fw);
  ctx.lineTo(fw, height - fw);
  ctx.lineTo(0, height);
  ctx.closePath();
  ctx.fill();

  // Right
  fillRail('right');
  ctx.beginPath();
  ctx.moveTo(width, 0);
  ctx.lineTo(width - fw, fw);
  ctx.lineTo(width - fw, height - fw);
  ctx.lineTo(width, height);
  ctx.closePath();
  ctx.fill();

  // Miter lines
  const miterOpacity = computeMiterOpacity(frameColor);
  ctx.strokeStyle = `rgba(0,0,0,${miterOpacity.toFixed(3)})`;
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(fw, fw);
  ctx.moveTo(width, 0);
  ctx.lineTo(width - fw, fw);
  ctx.moveTo(0, height);
  ctx.lineTo(fw, height - fw);
  ctx.moveTo(width, height);
  ctx.lineTo(width - fw, height - fw);
  ctx.stroke();

  // Generic 3D shading only when no profile — profile gradient already encodes the shape
  if (!useProfile) {
    add3DShading(ctx, width, height, fw, frameDepth, frameColor);
  }
}

/**
 * Parse a hex color to RGB components.
 * @param {string} hex - e.g. '#f5f0e8'
 * @returns {{r:number, g:number, b:number}}
 */
function hexToRgb(hex) {
  const v = parseInt((hex || '#000000').replace('#', ''), 16);
  return { r: (v >> 16) & 255, g: (v >> 8) & 255, b: v & 255 };
}

function hexToHSL(hex) {
  const { r, g, b } = hexToRgb(hex);
  const r1 = r / 255, g1 = g / 255, b1 = b / 255;
  const max = Math.max(r1, g1, b1), min = Math.min(r1, g1, b1);
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  const h = d === 0 ? 0
    : max === r1 ? (((g1 - b1) / d) % 6) * 60
    : max === g1 ? ((b1 - r1) / d + 2) * 60
    : ((r1 - g1) / d + 4) * 60;
  return { h: (h + 360) % 360, s, l };
}

function hslToHex(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r1, g1, b1;
  if (h < 60)       [r1, g1, b1] = [c, x, 0];
  else if (h < 120) [r1, g1, b1] = [x, c, 0];
  else if (h < 180) [r1, g1, b1] = [0, c, x];
  else if (h < 240) [r1, g1, b1] = [0, x, c];
  else if (h < 300) [r1, g1, b1] = [x, 0, c];
  else              [r1, g1, b1] = [c, 0, x];
  const r = Math.round((r1 + m) * 255);
  const g = Math.round((g1 + m) * 255);
  const b = Math.round((b1 + m) * 255);
  return `rgb(${r},${g},${b})`;
}

/**
 * Convert raw profile points to a sorted top-face lookup for gradient sampling.
 * Accepts [{x,y},...] (SVG-derived objects) or [[x,z],...] (legacy arrays).
 * y is SVG-convention: y=0 = front face (most protruding), y=1 = back face (against wall).
 * We keep min-y per x-bucket to extract the visible front face profile.
 * Returns [[x, y], ...] sorted by x ascending (0=outer → 1=inner).
 */
function normalizeProfilePoints(points) {
  if (!points || points.length === 0) return null;
  const isObj = typeof points[0] === 'object' && !Array.isArray(points[0]);
  const pairs = isObj ? points.map(p => [p.x, p.y]) : points;
  // Bucket by x (quantize to avoid float noise), keep max y (top face)
  // 32 bins (width ≈0.031): wide enough that top-face and back-face points at
  // similar x positions share a bucket; max-y then picks the top face.
  const map = new Map();
  for (const [x, y] of pairs) {
    const k = Math.round(x * 32) / 32;
    // y=0 is front face (SVG top), y=1 is back face — keep min-y to get the visible front face.
    if (!map.has(k) || y < map.get(k)) map.set(k, y);
  }
  const sorted = [...map.entries()].sort((a, b) => a[0] - b[0]);
  // Remove isolated high-y spikes (orphan back-face points that survived min-y selection)
  for (let i = 1; i < sorted.length - 1; i++) {
    if (sorted[i][1] > 0.9 && sorted[i - 1][1] < 0.5 && sorted[i + 1][1] < 0.5) {
      sorted[i][1] = (sorted[i - 1][1] + sorted[i + 1][1]) / 2;
    }
  }
  return sorted;
}

/**
 * Interpolate y from a sorted [[x,y],...] top-face profile at position t (0-1).
 * t=0 is the outer edge, t=1 is the inner (rebate) edge.
 */
function sampleProfile(sorted, t) {
  if (!sorted || sorted.length === 0) return 0.8;
  if (t <= sorted[0][0]) return sorted[0][1];
  if (t >= sorted[sorted.length - 1][0]) return sorted[sorted.length - 1][1];
  for (let i = 0; i < sorted.length - 1; i++) {
    const [x0, z0] = sorted[i], [x1, z1] = sorted[i + 1];
    if (t >= x0 && t <= x1) return z0 + (t - x0) / (x1 - x0) * (z1 - z0);
  }
  return 0.8;
}

/**
 * Build a Canvas LinearGradient across a frame rail using the moulding cross-section profile.
 * The gradient direction outer→inner is defined by (x0,y0)→(x1,y1).
 * Each stop's brightness is computed from the surface normal dotted against a per-rail light vector.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x0,y0 - gradient start (outer edge of rail)
 * @param {number} x1,y1 - gradient end   (inner edge of rail)
 * @param {Array}  profilePoints - [[x,z],...] normalized cross-section
 * @param {string} frameColor - hex color of the frame
 * @param {'top'|'bottom'|'left'|'right'} railSide
 */
function buildProfileGradient(ctx, x0, y0, x1, y1, profilePoints, frameColor, railSide) {
  const sorted = normalizeProfilePoints(profilePoints);
  if (!sorted) return null;

  // Per-rail base brightness: top is most lit, bottom least lit.
  // Multiplies the base lightness so rail-to-rail shading is preserved.
  const RAIL_BRIGHT = { top: 1.0, left: 0.88, right: 0.78, bottom: 0.68 };
  const railBright = RAIL_BRIGHT[railSide] ?? 1.0;

  const { h, s, l } = hexToHSL(frameColor);

  // Compute the actual y-range of the visible profile so we can normalize contrast.
  // This makes dark frames use their full available lightness swing instead of a fixed cap.
  const yValues = sorted.map(e => e[1]);
  const yMin = Math.min(...yValues);        // front face (most protruding)
  const yMax = Math.max(...yValues);        // most recessed visible point
  const yRange = Math.max(yMax - yMin, 0.05); // avoid div-by-zero on perfectly flat profiles

  // Shadow budget: swing from base lightness down to ~15% of base (perceptual dark limit).
  // Clamped so very light frames don't go pure black, dark frames still show contrast.
  const shadowBudget = Math.max(l * railBright * 0.70, 0.08);

  const grad = ctx.createLinearGradient(x0, y0, x1, y1);
  const N = 32;
  for (let i = 0; i <= N; i++) {
    const y = sampleProfile(sorted, i / N);
    // Normalize y into [0,1] over the profile's actual range, then scale to shadow budget
    const t = Math.max(0, Math.min(1, (y - yMin) / yRange));
    const newL = Math.max(0, Math.min(1, l * railBright - t * shadowBudget));
    grad.addColorStop(i / N, hslToHex(h, s, newL));
  }
  return grad;
}

/**
 * Create a shadow color string tinted by the wall color.
 * Mixes the wall color into black at the given ratio, then applies alpha.
 * @param {string} wallHex - Wall color hex
 * @param {number} alpha - Shadow opacity (0-1)
 * @param {number} tint - How much wall color to mix in (0 = pure black, 1 = full wall color). Default 0.25.
 * @returns {string} rgba() color string
 */
function tintedShadow(wallHex, alpha, tint = 0.25) {
  const c = hexToRgb(wallHex);
  const r = Math.round(c.r * tint);
  const g = Math.round(c.g * tint);
  const b = Math.round(c.b * tint);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Multi-pass canvas drop shadow. Renders four shadow layers behind the frame rect
 * on a solid background, producing a realistic composite shadow.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x - Frame rect x
 * @param {number} y - Frame rect y
 * @param {number} w - Frame rect width
 * @param {number} h - Frame rect height
 * @param {number} frameWidth - Frame rail width (scales shadow size)
 * @param {string} bgColor - Background color to re-fill over the rect
 * @param {number[]} [shadowDir] - [x, y] offset multipliers for directional shadow (default [0.4, 0.6])
 */
export function renderDropShadowCanvas(ctx, x, y, w, h, frameWidth, bgColor = '#f5f5f5', shadowDir) {
  const base = Math.max(8, frameWidth * 0.8);
  const sdx = shadowDir?.[0] ?? 0.4;
  const sdy = shadowDir?.[1] ?? 0.6;

  // Shadow color is tinted by the wall color for realism (warm walls = warm shadows).
  // Each pass: draw black rect with shadow, then erase rect with bgColor.
  // shadowDir [sdx, sdy] — light from upper-left → shadow falls lower-right.

  // Pass 1: Wide soft ambient glow (directional)
  ctx.save();
  ctx.shadowColor = tintedShadow(bgColor, 0.12);
  ctx.shadowBlur = base * 6;
  ctx.shadowOffsetX = base * sdx * 3;
  ctx.shadowOffsetY = base * sdy * 3;
  ctx.fillStyle = '#000';
  ctx.fillRect(x, y, w, h);
  ctx.restore();
  ctx.fillStyle = bgColor;
  ctx.fillRect(x, y, w, h);

  // Pass 2: Medium directional shadow
  ctx.save();
  ctx.shadowColor = tintedShadow(bgColor, 0.18);
  ctx.shadowBlur = base * 2.5;
  ctx.shadowOffsetX = base * sdx * 1.8;
  ctx.shadowOffsetY = base * sdy * 1.8;
  ctx.fillStyle = '#000';
  ctx.fillRect(x, y, w, h);
  ctx.restore();
  ctx.fillStyle = bgColor;
  ctx.fillRect(x, y, w, h);

  // Pass 3: Primary cast shadow (tight, strong directional)
  ctx.save();
  ctx.shadowColor = tintedShadow(bgColor, 0.30);
  ctx.shadowBlur = base * 1.2;
  ctx.shadowOffsetX = base * sdx * 1.0;
  ctx.shadowOffsetY = base * sdy * 1.0;
  ctx.fillStyle = '#000';
  ctx.fillRect(x, y, w, h);
  ctx.restore();
  ctx.fillStyle = bgColor;
  ctx.fillRect(x, y, w, h);

  // Pass 4: Contact shadow (crisp edge)
  ctx.save();
  ctx.shadowColor = tintedShadow(bgColor, 0.35);
  ctx.shadowBlur = base * 0.3;
  ctx.shadowOffsetX = base * sdx * 0.3;
  ctx.shadowOffsetY = base * sdy * 0.3;
  ctx.fillStyle = '#000';
  ctx.fillRect(x, y, w, h);
  ctx.restore();
  ctx.fillStyle = bgColor;
  ctx.fillRect(x, y, w, h);
}

/**
 * Render a radial spotlight wash over the entire canvas.
 * Simulates ambient room lighting — brightens center/upper-left, drawn as
 * the final compositing pass over wall + frame + artwork.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} canvasW - Full canvas width
 * @param {number} canvasH - Full canvas height
 * @param {number} frameCx - Frame center X
 * @param {number} frameCy - Frame center Y
 * @param {Object} [light] - Per-wall lighting config
 * @param {number[]} [light.lightPos] - [x%, y%] light direction (used to offset spot)
 * @param {number} [light.spotAlpha] - Peak opacity of the spotlight wash
 */
export function renderSpotlightWash(ctx, canvasW, canvasH, frameCx, frameCy, light) {
  const lp = light?.lightPos || [0.5, 0.3];
  const sa = light?.spotAlpha ?? 0.04;
  if (sa <= 0) return;

  ctx.save();
  // Offset spot toward the light source direction
  const spotX = frameCx + (lp[0] - 0.5) * canvasW * 0.3;
  const spotY = frameCy + (lp[1] - 0.5) * canvasH * 0.3;
  const spotRadius = Math.max(canvasW, canvasH) * 0.7;
  const grad = ctx.createRadialGradient(spotX, spotY, 0, spotX, spotY, spotRadius);
  grad.addColorStop(0, `rgba(255,255,255,${sa.toFixed(3)})`);
  grad.addColorStop(0.4, `rgba(255,255,255,${(sa * 0.55).toFixed(3)})`);
  grad.addColorStop(0.75, 'rgba(255,255,255,0)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, canvasW, canvasH);
  ctx.restore();
}

/**
 * Render ambient wall color bleed onto the frame/artwork area.
 * Simulates bounced light from the wall reflecting onto the frame edges
 * and nearby artwork. Draws four edge gradients from each side inward,
 * using the wall color at low opacity with a 'color' blend mode.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} fx - Frame outer x
 * @param {number} fy - Frame outer y
 * @param {number} fw - Frame outer width
 * @param {number} fh - Frame outer height
 * @param {string} wallColor - Wall color hex
 * @param {number} frameWidthPx - Frame rail width in px (scales bleed depth)
 */
export function renderAmbientBleed(ctx, fx, fy, fw, fh, wallColor, frameWidthPx, frameColor) {
  const c = hexToRgb(wallColor);
  const bleedDepth = frameWidthPx * 1.5;

  // Scale alpha by luminance contrast: light walls barely affect dark frames.
  // Relative luminance (ITU-R BT.709): 0.2126R + 0.7152G + 0.0722B
  let alpha = 0.08;
  if (frameColor) {
    const fc = hexToRgb(frameColor);
    const wallLum = (c.r * 0.2126 + c.g * 0.7152 + c.b * 0.0722) / 255;
    const frameLum = (fc.r * 0.2126 + fc.g * 0.7152 + fc.b * 0.0722) / 255;
    // When wall is much lighter than frame, scale down aggressively
    const contrast = Math.max(0, wallLum - frameLum);
    alpha *= 1 - contrast * 0.85;
  }

  const colorStr = `${c.r},${c.g},${c.b}`;

  ctx.save();
  // Clip to frame rect so bleed doesn't spill onto wall
  ctx.beginPath();
  ctx.rect(fx, fy, fw, fh);
  ctx.clip();

  // Left edge bleed
  const leftGrad = ctx.createLinearGradient(fx, fy, fx + bleedDepth, fy);
  leftGrad.addColorStop(0, `rgba(${colorStr},${alpha})`);
  leftGrad.addColorStop(1, `rgba(${colorStr},0)`);
  ctx.fillStyle = leftGrad;
  ctx.fillRect(fx, fy, bleedDepth, fh);

  // Right edge bleed
  const rightGrad = ctx.createLinearGradient(fx + fw, fy, fx + fw - bleedDepth, fy);
  rightGrad.addColorStop(0, `rgba(${colorStr},${alpha})`);
  rightGrad.addColorStop(1, `rgba(${colorStr},0)`);
  ctx.fillStyle = rightGrad;
  ctx.fillRect(fx + fw - bleedDepth, fy, bleedDepth, fh);

  // Top edge bleed
  const topGrad = ctx.createLinearGradient(fx, fy, fx, fy + bleedDepth);
  topGrad.addColorStop(0, `rgba(${colorStr},${alpha})`);
  topGrad.addColorStop(1, `rgba(${colorStr},0)`);
  ctx.fillStyle = topGrad;
  ctx.fillRect(fx, fy, fw, bleedDepth);

  // Bottom edge bleed (stronger — wall reflects up more)
  const botGrad = ctx.createLinearGradient(fx, fy + fh, fx, fy + fh - bleedDepth * 1.3);
  botGrad.addColorStop(0, `rgba(${colorStr},${(alpha * 1.2).toFixed(3)})`);
  botGrad.addColorStop(1, `rgba(${colorStr},0)`);
  ctx.fillStyle = botGrad;
  ctx.fillRect(fx, fy + fh - bleedDepth * 1.3, fw, bleedDepth * 1.3);

  ctx.restore();
}

/**
 * Render glass reflection overlay (diagonal white gradient).
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x - Rect x
 * @param {number} y - Rect y
 * @param {number} w - Rect width
 * @param {number} h - Rect height
 * @param {number} opacity - Peak opacity (default 0.06)
 */
export function renderGlassReflection(ctx, x, y, w, h, opacity = 0.06) {
  if (opacity <= 0) return;
  ctx.save();
  // Diagonal gradient from top-left to bottom-right
  const grad = ctx.createLinearGradient(x, y, x + w, y + h);
  grad.addColorStop(0, `rgba(255,255,255,${opacity})`);
  grad.addColorStop(0.3, `rgba(255,255,255,${(opacity * 0.3).toFixed(4)})`);
  grad.addColorStop(0.6, 'rgba(255,255,255,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(x, y, w, h);
  ctx.restore();
}

/**
 * Render mat/mount area with subtle eggshell texture.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x - Mat area x
 * @param {number} y - Mat area y
 * @param {number} w - Mat area width
 * @param {number} h - Mat area height
 * @param {string} [color='#fafaf8'] - Mat base colour
 */
export function renderMatTexture(ctx, x, y, w, h, color = '#fafaf8') {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, h);

  // Subtle dot pattern — slight darkening overlay, works on any base colour.
  // The 4×4 tile is static, so build it once at module level; this runs on
  // every render (twice for double mounts) and previously allocated a fresh
  // GPU-backed canvas each time. createPattern stays per-call — patterns are
  // bound to the context that created them.
  if (!_matDotTile) {
    _matDotTile = document.createElement('canvas');
    _matDotTile.width = 4;
    _matDotTile.height = 4;
    const tc = _matDotTile.getContext('2d');
    tc.fillStyle = 'rgba(0,0,0,0.06)';
    tc.fillRect(0, 0, 1, 1);
  }
  ctx.fillStyle = ctx.createPattern(_matDotTile, 'repeat');
  ctx.fillRect(x, y, w, h);
}

/**
 * Render inner edge shadows where the frame meets the mat/artwork.
 * Call this AFTER drawing the mat and artwork so the shadows appear on top.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x - Inner edge x (frame opening left)
 * @param {number} y - Inner edge y (frame opening top)
 * @param {number} w - Frame opening width
 * @param {number} h - Frame opening height
 * @param {number} fw - Frame rail width (used to scale shadow size)
 * @param {number} frameDepth - Depth factor 0-1
 */
export function renderInnerEdgeShadows(ctx, x, y, w, h, fw, frameDepth = 0.5, frameColor = '') {
  const fd = frameDepth;
  const lum = luminance(frameColor);

  // Shadow sizes: base (3% of opening dimension) + frame-width-scaled component.
  // The Svelte uses a fixed 0.3 SVG-unit base (~3% of artwork) that prevents
  // shadows from becoming invisible on thin frames. The fw-scaled component
  // adds depth-proportional shadow on top.
  const basePx = Math.max(w, h) * 0.03;
  const topShadowH = Math.max(8, basePx + fw * 0.5 * fd);
  const leftShadowW = Math.max(6, basePx * 0.83 + fw * 0.4 * fd);

  // Top inner shadow (gradient stops match Svelte SVG innerShadowTop)
  const topGrad = ctx.createLinearGradient(x, y, x, y + topShadowH);
  topGrad.addColorStop(0, `rgba(0,0,0,${(0.6 * fd + 0.15).toFixed(3)})`);
  topGrad.addColorStop(0.25, `rgba(0,0,0,${(0.3 * fd + 0.08).toFixed(3)})`);
  topGrad.addColorStop(0.6, `rgba(0,0,0,${(0.1 * fd).toFixed(3)})`);
  topGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = topGrad;
  ctx.fillRect(x, y, w, topShadowH);

  // Left inner shadow (gradient stops match Svelte SVG innerShadowLeft)
  const leftGrad = ctx.createLinearGradient(x, y, x + leftShadowW, y);
  leftGrad.addColorStop(0, `rgba(0,0,0,${(0.5 * fd + 0.12).toFixed(3)})`);
  leftGrad.addColorStop(0.25, `rgba(0,0,0,${(0.25 * fd + 0.06).toFixed(3)})`);
  leftGrad.addColorStop(0.6, `rgba(0,0,0,${(0.08 * fd).toFixed(3)})`);
  leftGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = leftGrad;
  ctx.fillRect(x, y, leftShadowW, h);

  // Bottom highlight — very subtle light catch on inner lip
  // Scale by luminance so it vanishes on dark frames (white on black = harsh line)
  const highlightH = Math.max(0.5, Math.min(w, h) * 0.005);
  ctx.fillStyle = `rgba(255,255,255,${(0.015 * fd * lum).toFixed(4)})`;
  ctx.fillRect(x, y + h - highlightH, w, highlightH);

  // Right highlight — barely perceptible
  const highlightW = Math.max(0.5, Math.min(w, h) * 0.004);
  ctx.fillStyle = `rgba(255,255,255,${(0.01 * fd * lum).toFixed(4)})`;
  ctx.fillRect(x + w - highlightW, y, highlightW, h);
}

/**
 * Render mount depth shadow at the artwork opening.
 * The mount/mat board sits ~2.4mm (0.094") above the artwork surface. With light
 * from top-left, the top and left edges cast shadow onto the artwork below.
 * Includes crisp bevel edge lines and bottom/right highlights matching the Svelte
 * FrameSlot implementation.
 *
 * Call this AFTER drawing the artwork so the shadow appears on top of it.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x - Artwork opening x
 * @param {number} y - Artwork opening y
 * @param {number} w - Artwork opening width
 * @param {number} h - Artwork opening height
 * @param {number} frameDepth - Depth factor 0-1 (default 0.5)
 * @param {number} pxPerInch - Pixels per inch (for physical sizing). If 0, estimated from w.
 */
export function renderMountDepthShadow(ctx, x, y, w, h, frameDepth = 0.5, pxPerInch = 0, mountDepthInches = 0.094) {
  const fd = frameDepth;
  const matDepthInches = mountDepthInches;
  const recessSize = matDepthInches * (0.6 + 0.4 * fd);
  const bevelLineInches = 0.03;

  // Convert to pixels
  const scale = pxPerInch > 0 ? pxPerInch : w / 16;
  const recessSizePx = Math.max(2, recessSize * scale);
  const bevelLinePx = Math.max(1, bevelLineInches * scale);

  const recessOpacity = 0.25 + 0.15 * fd;
  const leftOpacity = 0.20 + 0.12 * fd;
  const bevelHighlight = 0.03 + 0.02 * fd;

  // Top recess shadow
  const topGrad = ctx.createLinearGradient(x, y, x, y + recessSizePx);
  topGrad.addColorStop(0, `rgba(0,0,0,${recessOpacity.toFixed(3)})`);
  topGrad.addColorStop(0.4, `rgba(0,0,0,${(recessOpacity * 0.4).toFixed(3)})`);
  topGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = topGrad;
  ctx.fillRect(x, y, w, recessSizePx);

  // Left recess shadow
  const leftGrad = ctx.createLinearGradient(x, y, x + recessSizePx, y);
  leftGrad.addColorStop(0, `rgba(0,0,0,${leftOpacity.toFixed(3)})`);
  leftGrad.addColorStop(0.4, `rgba(0,0,0,${(leftOpacity * 0.4).toFixed(3)})`);
  leftGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = leftGrad;
  ctx.fillRect(x, y, recessSizePx, h);

  // Crisp bevel edge lines (top and left)
  ctx.strokeStyle = `rgba(0,0,0,${recessOpacity.toFixed(3)})`;
  ctx.lineWidth = bevelLinePx;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + w, y);
  ctx.stroke();

  ctx.strokeStyle = `rgba(0,0,0,${leftOpacity.toFixed(3)})`;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x, y + h);
  ctx.stroke();

  // Bottom bevel highlight (45-degree cut catches light)
  ctx.fillStyle = `rgba(255,255,255,${bevelHighlight.toFixed(3)})`;
  ctx.fillRect(x, y + h - bevelLinePx, w, bevelLinePx);

  // Right bevel highlight
  ctx.fillStyle = `rgba(255,255,255,${(bevelHighlight * 0.7).toFixed(3)})`;
  ctx.fillRect(x + w - bevelLinePx, y, bevelLinePx, h);
}

/**
 * Render a realistic painted wall texture onto the canvas.
 * Layers: base color → tiled noise PNG (overlay blend) → directional lighting → vignette.
 *
 * The noise tile (wall-texture-tile.png) is a 256×256 greyscale image pre-generated
 * by scripts/generate-wall-texture.js. It is loaded via loadImage() and cached in
 * textureCache like any other asset.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} width - Canvas width in px
 * @param {number} height - Canvas height in px
 * @param {string} color - Wall color hex string
 * @param {HTMLImageElement|null} textureTile - Pre-loaded wall texture tile image
 * @param {Object} [light] - Per-wall lighting config
 * @param {number} [light.lightingAlpha] - Opacity for room lighting map multiply blend (0-1, default 0.5)
 * @param {HTMLImageElement|null} [lightingMap] - Pre-loaded room lighting map PNG
 */
export function renderWallTexture(ctx, width, height, color, textureTile, light, lightingMap, frameFraction = 0.80, frameOffsetY = 0) {
  const ta = light?.textureAlpha ?? 1.0; // per-wall texture intensity scalar (0-1)

  // 1. Base color fill
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, width, height);

  // 2. Painted plaster texture via two blend passes of tiled PNG
  if (textureTile) {
    const pattern = ctx.createPattern(textureTile, 'repeat');

    // Pass A: overlay blend — mid-grey neutral, lighter pixels brighten, darker darken
    ctx.save();
    ctx.globalCompositeOperation = 'overlay';
    ctx.globalAlpha = 0.35 * ta;
    ctx.fillStyle = pattern;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();

    // Pass B: soft-light blend — adds more subtle surface variation on top
    ctx.save();
    ctx.globalCompositeOperation = 'soft-light';
    ctx.globalAlpha = 0.20 * ta;
    ctx.fillStyle = pattern;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  }

  // 3. Room lighting map — a pre-made greyscale PNG applied via multiply blend.
  //    White pixels leave the wall unchanged, darker pixels darken it.
  //    After multiply, re-stamp the wall's hue/saturation via 'color' blend
  //    to prevent the per-channel multiply from shifting the wall color.
  if (lightingMap) {
    const la = light?.lightingAlpha ?? 0.5;

    // Scale & pan the lighting map to match frame zoom/offset
    const zoomRatio = frameFraction / 0.80;
    const lw = width * zoomRatio;
    const lh = height * zoomRatio;
    const lx = (width - lw) / 2;
    const ly = (height - lh) / 2 + frameOffsetY * height;

    // Darken via multiply
    ctx.save();
    ctx.globalCompositeOperation = 'multiply';
    ctx.globalAlpha = la;
    ctx.drawImage(lightingMap, lx, ly, lw, lh);
    ctx.restore();

    // Restore original hue/saturation — 'color' takes H+S from source, L from dest
    ctx.save();
    ctx.globalCompositeOperation = 'color';
    ctx.globalAlpha = la;
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  }
}

/**
 * Generate a reusable canvas texture tile simulating linen/canvas weave.
 * Creates a 128x128 offscreen canvas with procedural horizontal and vertical
 * thread lines plus fine noise for organic feel. Cached in textureCache.
 *
 * @param {number} [size=128] - Tile size in pixels
 * @returns {HTMLCanvasElement} Tileable canvas texture
 */
export function generateCanvasTextureTile(size = 128) {
  // Cached outside the LRU textureCache: the tile is procedural noise, so an
  // eviction (50+ moulding URLs in a session) regenerated it with DIFFERENT
  // random noise — a visible flicker in the canvas texture overlay.
  if (_canvasTextureTile) return _canvasTextureTile;

  const tile = document.createElement('canvas');
  tile.width = size;
  tile.height = size;
  const ctx = tile.getContext('2d');

  // Fill with neutral mid-grey — the "no effect" base for soft-light blending
  ctx.fillStyle = '#808080';
  ctx.fillRect(0, 0, size, size);

  // Diagonal diamond/twill weave: two sets of parallel lines at ±45°
  // creates the characteristic diamond-cell canvas texture visible on
  // real stretched canvas prints. Sized to be clearly visible at normal
  // viewing distance.
  const spacing = 6;   // px between parallel threads
  const lineW = 1.2;   // visible thread width
  ctx.lineWidth = lineW;

  // ↘ threads (top-left → bottom-right at 45°)
  for (let d = -size; d < size * 2; d += spacing) {
    // Dark groove between threads
    ctx.strokeStyle = 'rgba(0,0,0,0.35)';
    ctx.beginPath();
    ctx.moveTo(d, 0);
    ctx.lineTo(d + size, size);
    ctx.stroke();
    // Light ridge offset — thread catching light
    ctx.strokeStyle = 'rgba(255,255,255,0.20)';
    ctx.beginPath();
    ctx.moveTo(d + 2, 0);
    ctx.lineTo(d + size + 2, size);
    ctx.stroke();
  }

  // ↙ threads (top-right → bottom-left at -45°)
  for (let d = -size; d < size * 2; d += spacing) {
    ctx.strokeStyle = 'rgba(0,0,0,0.30)';
    ctx.beginPath();
    ctx.moveTo(d, 0);
    ctx.lineTo(d - size, size);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(255,255,255,0.18)';
    ctx.beginPath();
    ctx.moveTo(d - 2, 0);
    ctx.lineTo(d - size - 2, size);
    ctx.stroke();
  }

  // Light per-pixel noise for organic variation
  const imgData = ctx.getImageData(0, 0, size, size);
  const data = imgData.data;
  for (let i = 0; i < data.length; i += 4) {
    const noise = (Math.random() - 0.5) * 10;
    data[i] = Math.min(255, Math.max(0, data[i] + noise));
    data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + noise));
    data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + noise));
  }
  ctx.putImageData(imgData, 0, 0);

  _canvasTextureTile = tile;
  return tile;
}

/**
 * Render canvas texture overlay on top of artwork.
 * Tiles the procedural linen texture using overlay blend mode at low opacity
 * to simulate art printed/wrapped onto textured canvas fabric.
 *
 * Call AFTER drawing the artwork image.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x - Artwork area x
 * @param {number} y - Artwork area y
 * @param {number} w - Artwork area width
 * @param {number} h - Artwork area height
 * @param {number} [opacity=0.18] - Texture overlay opacity
 */
export function renderCanvasTexture(ctx, x, y, w, h, opacity = 0.65) {
  const tile = generateCanvasTextureTile();
  const pattern = ctx.createPattern(tile, 'repeat');

  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.globalCompositeOperation = 'soft-light';
  ctx.globalAlpha = opacity;
  // Scale pattern down 50% so the weave appears finer / more realistic
  ctx.translate(x, y);
  ctx.scale(0.5, 0.5);
  ctx.fillStyle = pattern;
  ctx.fillRect(0, 0, w / 0.5, h / 0.5);
  ctx.restore();
}

/**
 * Render the visible canvas wrap edge on all four sides.
 * Shows darkened, slightly blurred continuation of the artwork wrapping
 * around the stretcher bars, with a subtle highlight at the outermost edge.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x - Canvas edge outer x (before artwork area)
 * @param {number} y - Canvas edge outer y
 * @param {number} w - Total area width (artwork + 2*edgeWidth)
 * @param {number} h - Total area height (artwork + 2*edgeWidth)
 * @param {number} edgeWidth - Edge width in px (~3mm visible wrap)
 * @param {HTMLImageElement|null} artworkImage - Artwork image for edge continuation
 * @param {number} artworkAspect - Artwork aspect ratio (w/h)
 */
export function renderCanvasEdge(ctx, x, y, w, h, edgeWidth) {
  if (edgeWidth < 0.5) return;

  // The artwork is drawn full-bleed underneath covering the edge zone.
  // The canvas edge simulates the artwork curving around the stretcher bars.
  // We darken the artwork progressively toward the outer edge rather than
  // painting opaque grey, so the artwork shows through naturally.
  //
  // Note: No ctx.clip() — clip boundaries create subpixel rendering artifacts
  // visible as faint lines. The gradients fade to 0 at the inner edge so the
  // fillRect alone constrains the visible area.

  // Top edge — darkening gradient
  const topGrad = ctx.createLinearGradient(x, y + edgeWidth, x, y);
  topGrad.addColorStop(0, 'rgba(0,0,0,0)');
  topGrad.addColorStop(0.4, 'rgba(0,0,0,0.12)');
  topGrad.addColorStop(1, 'rgba(0,0,0,0.30)');
  ctx.fillStyle = topGrad;
  ctx.fillRect(x, y, w, edgeWidth);

  // Bottom edge
  const botGrad = ctx.createLinearGradient(x, y + h - edgeWidth, x, y + h);
  botGrad.addColorStop(0, 'rgba(0,0,0,0)');
  botGrad.addColorStop(0.4, 'rgba(0,0,0,0.12)');
  botGrad.addColorStop(1, 'rgba(0,0,0,0.30)');
  ctx.fillStyle = botGrad;
  ctx.fillRect(x, y + h - edgeWidth, w, edgeWidth);

  // Left edge
  const leftGrad = ctx.createLinearGradient(x + edgeWidth, y, x, y);
  leftGrad.addColorStop(0, 'rgba(0,0,0,0)');
  leftGrad.addColorStop(0.4, 'rgba(0,0,0,0.12)');
  leftGrad.addColorStop(1, 'rgba(0,0,0,0.30)');
  ctx.fillStyle = leftGrad;
  ctx.fillRect(x, y, edgeWidth, h);

  // Right edge
  const rightGrad = ctx.createLinearGradient(x + w - edgeWidth, y, x + w, y);
  rightGrad.addColorStop(0, 'rgba(0,0,0,0)');
  rightGrad.addColorStop(0.4, 'rgba(0,0,0,0.12)');
  rightGrad.addColorStop(1, 'rgba(0,0,0,0.30)');
  ctx.fillStyle = rightGrad;
  ctx.fillRect(x + w - edgeWidth, y, edgeWidth, h);
}

/**
 * Render float gap between canvas and float frame.
 * Draws four thin dark strips around the canvas with subtle inner shadow.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x - Float gap outer x (frame inner edge)
 * @param {number} y - Float gap outer y
 * @param {number} w - Float gap total width
 * @param {number} h - Float gap total height
 * @param {number} gapWidth - Gap width in px (~5mm physical)
 */
export function renderFloatGap(ctx, x, y, w, h, gapWidth) {
  if (gapWidth < 0.5) return;

  // 460f: previous rgb(15,15,15) painted the float gap as a near-black
  // band — on light-coloured frames (oak, white) it read as a thick
  // black border around the canvas, misleading buyers who didn't pick
  // a black frame. Use a mid-grey instead and let the directional
  // shadow gradients carry the depth cue.
  ctx.fillStyle = 'rgb(58,52,46)';
  // Top
  ctx.fillRect(x, y, w, gapWidth);
  // Bottom
  ctx.fillRect(x, y + h - gapWidth, w, gapWidth);
  // Left (inset to avoid overlapping top/bottom corners)
  ctx.fillRect(x, y + gapWidth, gapWidth, h - gapWidth * 2);
  // Right
  ctx.fillRect(x + w - gapWidth, y + gapWidth, gapWidth, h - gapWidth * 2);

  // Directional shadows on the canvas-facing edges — these carry the
  // "recessed" depth read so the gap itself doesn't need to be black.
  const shadowDepth = gapWidth * 0.6;

  const topGrad = ctx.createLinearGradient(x, y + gapWidth, x, y + gapWidth + shadowDepth);
  topGrad.addColorStop(0, 'rgba(0,0,0,0.35)');
  topGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = topGrad;
  ctx.fillRect(x + gapWidth, y + gapWidth, w - gapWidth * 2, shadowDepth);

  const leftGrad = ctx.createLinearGradient(x + gapWidth, y, x + gapWidth + shadowDepth, y);
  leftGrad.addColorStop(0, 'rgba(0,0,0,0.28)');
  leftGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = leftGrad;
  ctx.fillRect(x + gapWidth, y + gapWidth, shadowDepth, h - gapWidth * 2);
}

/**
 * Lighter drop shadow for unframed canvas on stretcher bars.
 * 2-pass shadow (soft ambient + medium directional) vs 4-pass for framed art.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x - Canvas rect x
 * @param {number} y - Canvas rect y
 * @param {number} w - Canvas rect width
 * @param {number} h - Canvas rect height
 * @param {string} bgColor - Background/wall color hex
 * @param {number[]} [shadowDir] - [x, y] offset multipliers
 */
export function renderCanvasDropShadow(ctx, x, y, w, h, bgColor = '#f5f5f5', shadowDir) {
  const base = Math.max(6, Math.min(w, h) * 0.02);
  const sdx = shadowDir?.[0] ?? 0.4;
  const sdy = shadowDir?.[1] ?? 0.6;

  // Pass 1: Soft ambient
  ctx.save();
  ctx.shadowColor = tintedShadow(bgColor, 0.10);
  ctx.shadowBlur = base * 4;
  ctx.shadowOffsetX = base * sdx * 2;
  ctx.shadowOffsetY = base * sdy * 2;
  ctx.fillStyle = '#000';
  ctx.fillRect(x, y, w, h);
  ctx.restore();
  ctx.fillStyle = bgColor;
  ctx.fillRect(x, y, w, h);

  // Pass 2: Medium directional
  ctx.save();
  ctx.shadowColor = tintedShadow(bgColor, 0.18);
  ctx.shadowBlur = base * 1.5;
  ctx.shadowOffsetX = base * sdx * 1.0;
  ctx.shadowOffsetY = base * sdy * 1.0;
  ctx.fillStyle = '#000';
  ctx.fillRect(x, y, w, h);
  ctx.restore();
  ctx.fillStyle = bgColor;
  ctx.fillRect(x, y, w, h);
}

/**
 * Draw an image with cover-crop (like CSS object-fit:cover).
 * Centers the image and crops the overflow rather than stretching.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {HTMLImageElement} img
 * @param {number} dx - Destination x
 * @param {number} dy - Destination y
 * @param {number} dw - Destination width
 * @param {number} dh - Destination height
 */
export function drawImageCover(ctx, img, dx, dy, dw, dh) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) { ctx.drawImage(img, dx, dy, dw, dh); return; }
  const scale = Math.max(dw / iw, dh / ih);
  const sw = dw / scale;
  const sh = dh / scale;
  const sx = (iw - sw) / 2;
  const sy = (ih - sh) / 2;
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

/**
 * High-level art print frame compositor.
 * Draws complete framed/unframed art print given pre-computed pixel rects.
 * Both room-background-mock and gallery-wall-frame-mock delegate to this
 * function, passing environment-specific options (shadow direction, opening
 * fill, pixel-snap) to produce identical rendering sequences.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Object} opts
 * @param {Object|null} opts.frame      - { x, y, w, h } outer frame rect (null if unframed)
 * @param {Object} opts.opening         - { x, y, w, h } frame opening / paper rect
 * @param {Object} opts.artwork         - { x, y, w, h } artwork visible area (inset by mat)
 * @param {number} opts.frameWidthPx
 * @param {number} opts.matWidthPx
 * @param {boolean} opts.hasFrame
 * @param {string} opts.frameColor
 * @param {number} opts.frameDepth
 * @param {Image|null} opts.mouldingTexture
 * @param {Image|null} opts.artworkImage
 * @param {number} opts.glassReflection  - 0 = none
 * @param {string} opts.borderOption     - 'border' | 'no-border' | ''
 * @param {number} opts.pxPerInch        - for mount depth shadow scaling
 * @param {Object} [opts.shadow]         - { wallColor, shadowDir } for gallery-wall style shadow
 * @param {string} [opts.openingFill]    - fill behind mat/artwork (e.g. '#e8e4dc' for room mock)
 * @param {boolean} [opts.pixelSnap]     - Math.floor/ceil artwork rect (gallery-wall uses this)
 * @param {boolean} [opts.skipShadow]    - skip drop shadow (e.g. thumbnail mode)
 */
export function renderArtPrintFrame(ctx, opts) {
  const { frame, opening, artwork, frameWidthPx, matWidthPx, hasFrame,
          frameColor, frameDepth, profilePoints, mouldingTexture, artworkImage,
          glassReflection, borderOption, pxPerInch, shadow,
          openingFill, pixelSnap, skipShadow, matColor, matColorInner } = opts;

  // 1. Drop shadow
  if (!skipShadow) {
    if (hasFrame && frameWidthPx > 0) {
      renderDropShadowCanvas(ctx, frame.x, frame.y, frame.w, frame.h,
        frameWidthPx, shadow?.wallColor, shadow?.shadowDir);
    } else if (!hasFrame) {
      renderCanvasDropShadow(ctx, opening.x, opening.y, opening.w, opening.h,
        shadow?.wallColor, shadow?.shadowDir);
    }
  }

  // 2. Frame moulding
  if (hasFrame && frameWidthPx > 0) {
    ctx.save();
    ctx.translate(frame.x, frame.y);
    if (mouldingTexture) {
      renderTexturedFrameCanvas(ctx, frame.w, frame.h, frameWidthPx, mouldingTexture, frameColor, frameDepth, profilePoints);
    } else {
      renderSolidFrameCanvas(ctx, frame.w, frame.h, frameWidthPx, frameColor, frameDepth, profilePoints);
    }
    ctx.restore();
  }

  // 3. Opening fill (room mock fills behind mat; gallery-wall lets wall show through)
  if (openingFill) {
    ctx.fillStyle = openingFill;
    ctx.fillRect(opening.x, opening.y, opening.w, opening.h);
  }

  // 4. Mat texture
  if (matWidthPx > 0) {
    renderMatTexture(ctx, opening.x, opening.y, opening.w, opening.h, matColor);
    // Double / double-deep mount: draw inner mat band with a second colour.
    // Spec target (museum framing convention): outer reveal ~28mm, inner ~14mm.
    // → outer band ≈ 2/3 of total mat width, inner band ≈ 1/3 (inner ≈ 50% of outer).
    // We also enforce a minimum inner band of ~3px so the second colour stays
    // visible on small mats; if matWidthPx is too small for both bands, we
    // skip the inner draw rather than render a sliver.
    if ((borderOption === 'double-mount' || borderOption === 'double-deep-mount') && matColorInner) {
      const INNER_BAND_RATIO = 1 / 3; // inner band = 1/3 of total mat → ½ of outer
      const MIN_INNER_BAND_PX = 3;
      const innerBand = Math.max(MIN_INNER_BAND_PX, Math.round(matWidthPx * INNER_BAND_RATIO));
      const outerReveal = matWidthPx - innerBand;
      if (outerReveal > 0 && innerBand > 0 && opening.w - outerReveal * 2 > 0 && opening.h - outerReveal * 2 > 0) {
        renderMatTexture(ctx,
          opening.x + outerReveal, opening.y + outerReveal,
          opening.w - outerReveal * 2, opening.h - outerReveal * 2,
          matColorInner
        );
        // Thin shadow line at outer/inner mat boundary so the second band
        // is visible even when both mats are the same colour. Mimics the
        // bevel cut shadow you see in real double-mat framing.
        ctx.save();
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.12)';
        ctx.lineWidth = 1;
        ctx.strokeRect(
          opening.x + outerReveal + 0.5, opening.y + outerReveal + 0.5,
          opening.w - outerReveal * 2 - 1, opening.h - outerReveal * 2 - 1
        );
        ctx.restore();
      }
    }
  }

  // 5. Artwork — compute final rect (with optional pixel-snap)
  let ax = artwork.x, ay = artwork.y, aw = artwork.w, ah = artwork.h;
  if (pixelSnap) {
    ax = Math.floor(ax); ay = Math.floor(ay);
    aw = Math.ceil(artwork.x + artwork.w) - ax;
    ah = Math.ceil(artwork.y + artwork.h) - ay;
  }
  if (borderOption === 'mirror') {
    // Mirror insert: silver gradient with diagonal highlight — no artwork shown
    const mg = ctx.createLinearGradient(ax, ay, ax + aw, ay + ah);
    mg.addColorStop(0,   '#d0d4d8');
    mg.addColorStop(0.3, '#eaecee');
    mg.addColorStop(0.5, '#f8f8f8');
    mg.addColorStop(0.7, '#dfe2e5');
    mg.addColorStop(1,   '#c8cdd2');
    ctx.fillStyle = mg;
    ctx.fillRect(ax, ay, aw, ah);
    const hl = ctx.createLinearGradient(ax, ay, ax + aw * 0.5, ay + ah * 0.5);
    hl.addColorStop(0, 'rgba(255,255,255,0.45)');
    hl.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = hl;
    ctx.fillRect(ax, ay, aw, ah);
  } else if (artworkImage) {
    drawImageCover(ctx, artworkImage, ax, ay, aw, ah);
  } else if (borderOption !== 'border') {
    // Empty state — paint a soft warm-cream rectangle so the inside of the
    // frame doesn't read as a silver "mirror" insert (the previous neutral-
    // grey fallback was easily confused with the mirror border option).
    ctx.fillStyle = '#f3eee6';
    ctx.fillRect(ax, ay, aw, ah);
  }

  // 6. Mount depth shadow (uses snapped coordinates when applicable)
  if (matWidthPx > 0 && borderOption !== 'border' && frameDepth > 0) {
    const isDeepMount = borderOption === 'deep-mount' || borderOption === 'double-deep-mount';
    const mountDepthInches = isDeepMount ? 0.22 : 0.094; // deep = ~5.5mm, standard = 2.4mm
    renderMountDepthShadow(ctx, ax, ay, aw, ah, frameDepth, pxPerInch, mountDepthInches);
  }

  // 7. Inner edge shadows
  if (hasFrame && frameWidthPx > 0) {
    renderInnerEdgeShadows(ctx, opening.x, opening.y, opening.w, opening.h, frameWidthPx, frameDepth, frameColor);
  }

  // 8. Glass reflection
  if (hasFrame && frameWidthPx > 0 && glassReflection > 0) {
    renderGlassReflection(ctx, opening.x, opening.y, opening.w, opening.h, glassReflection);
  }
}

/**
 * High-level canvas product compositor (framed with float frame, or unframed stretcher bars).
 * Both room-background-mock and gallery-wall-frame-mock delegate to this function.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {Object} opts
 * @param {Object} opts.outer           - { x, y, w, h } outermost rect (frame outer or canvas outer)
 * @param {Object} opts.edge            - { x, y, w, h } canvas edge area (artwork + edge wrap)
 * @param {number} opts.frameWidthPx
 * @param {number} opts.floatGapPx
 * @param {number} opts.canvasEdgePx
 * @param {boolean} opts.hasFrame
 * @param {string} opts.frameColor
 * @param {number} opts.frameDepth       - typically 0.85 for canvas
 * @param {Image|null} opts.mouldingTexture
 * @param {Image|null} opts.artworkImage
 * @param {Object} [opts.shadow]         - { wallColor, shadowDir }
 * @param {string} [opts.edgeFill]       - fill behind edge area (e.g. '#f5f5f5')
 * @param {number} [opts.textureOpacity] - canvas texture opacity (undefined = default 0.65)
 * @param {boolean} [opts.borderLine]    - draw subtle outer border stroke
 * @param {number} [opts.borderLineWidth] - stroke width for border line
 * @param {boolean} [opts.skipShadow]    - skip drop shadow (e.g. thumbnail mode)
 */
export function renderCanvasProductFrame(ctx, opts) {
  const { outer, edge, frameWidthPx, floatGapPx, canvasEdgePx,
          hasFrame, frameColor, frameDepth, mouldingTexture, artworkImage,
          shadow, edgeFill, textureOpacity, borderLine, borderLineWidth,
          skipShadow } = opts;

  if (hasFrame) {
    // 1. Drop shadow
    if (!skipShadow) {
      renderDropShadowCanvas(ctx, outer.x, outer.y, outer.w, outer.h,
        frameWidthPx, shadow?.wallColor, shadow?.shadowDir);
    }

    // 2. Float frame
    if (frameWidthPx > 0) {
      ctx.save();
      ctx.translate(outer.x, outer.y);
      if (mouldingTexture) {
        renderTexturedFrameCanvas(ctx, outer.w, outer.h, frameWidthPx, mouldingTexture, frameColor, frameDepth);
      } else {
        renderSolidFrameCanvas(ctx, outer.w, outer.h, frameWidthPx, frameColor, frameDepth);
      }
      ctx.restore();
    }

    // 3. Float gap
    const gapX = outer.x + frameWidthPx;
    const gapY = outer.y + frameWidthPx;
    const gapW = outer.w - frameWidthPx * 2;
    const gapH = outer.h - frameWidthPx * 2;
    renderFloatGap(ctx, gapX, gapY, gapW, gapH, floatGapPx);

    // 4. Edge background fill
    if (edgeFill) {
      ctx.fillStyle = edgeFill;
      ctx.fillRect(edge.x, edge.y, edge.w, edge.h);
    }

    // 5. Artwork (full-bleed over edge area — canvas wraps around)
    if (artworkImage) {
      drawImageCover(ctx, artworkImage, edge.x, edge.y, edge.w, edge.h);
    }

    // 6. Canvas edge darkening
    renderCanvasEdge(ctx, edge.x, edge.y, edge.w, edge.h, canvasEdgePx);

    // 7. Border line
    if (borderLine) {
      ctx.strokeStyle = 'rgba(0,0,0,0.10)';
      ctx.lineWidth = borderLineWidth || 0.5;
      ctx.strokeRect(edge.x, edge.y, edge.w, edge.h);
    }

    // 8. Canvas texture
    renderCanvasTexture(ctx, edge.x, edge.y, edge.w, edge.h, textureOpacity);

    // 9. Inner edge shadows
    if (frameWidthPx > 0) {
      renderInnerEdgeShadows(ctx, gapX, gapY, gapW, gapH, frameWidthPx, frameDepth, frameColor);
    }

  } else {
    // --- UNFRAMED ---

    // 1. Drop shadow (lighter 2-pass)
    if (!skipShadow) {
      renderCanvasDropShadow(ctx, edge.x, edge.y, edge.w, edge.h,
        shadow?.wallColor, shadow?.shadowDir);
    }

    // 2. Artwork full-bleed
    if (artworkImage) {
      drawImageCover(ctx, artworkImage, edge.x, edge.y, edge.w, edge.h);
    }

    // 3. Canvas edge darkening
    renderCanvasEdge(ctx, edge.x, edge.y, edge.w, edge.h, canvasEdgePx);

    // 4. Border line
    if (borderLine) {
      ctx.strokeStyle = 'rgba(0,0,0,0.10)';
      ctx.lineWidth = borderLineWidth || 0.5;
      ctx.strokeRect(edge.x, edge.y, edge.w, edge.h);
    }

    // 5. Canvas texture
    renderCanvasTexture(ctx, edge.x, edge.y, edge.w, edge.h, textureOpacity);
  }
}

// Register on window for Shopify CDN compatibility (assets can't use relative imports)
window.FrameRendering = {
  loadImage,
  getCachedImage,
  computeMiterOpacity,
  add3DShading,
  renderTexturedFrameCanvas,
  renderSolidFrameCanvas,
  renderDropShadowCanvas,
  renderGlassReflection,
  renderMatTexture,
  renderInnerEdgeShadows,
  renderMountDepthShadow,
  renderWallTexture,
  renderSpotlightWash,
  renderAmbientBleed,
  generateCanvasTextureTile,
  renderCanvasTexture,
  renderCanvasEdge,
  renderFloatGap,
  renderCanvasDropShadow,
  drawImageCover,
  renderArtPrintFrame,
  renderCanvasProductFrame
};
